// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {Fragment, useState} from 'react';
import {Editor} from '../../../shared/editor/editor';
import {Button} from '../../../shared/button';
import {get_img_url} from './utils';
import {Accordion_panel} from '../ui/accordion/accordion';
import {build_support_link} from '../support_report';
import styles from './bounty_vulnerability_criteria.module.less';

const notice_icon_src = 'bounty_info.svg';
const left_column_size = 9;
const item_icon_src = {
  allowed: 'bounty_big_check.svg',
  blocked: 'bounty_x_close.svg',
};

export interface i_bounty_criteria_group {
  title: string;
  description?: string;
  items?: string[];
  type?: 'allowed'|'blocked';
  image_src?: string;
  image_alt?: string;
  show_text?: string;
  hide_text?: string;
}

export interface i_bounty_vulnerability_criteria_props {
  groups?: i_bounty_criteria_group[];
  notice?: string;
  cta_text?: string;
}

export const Bounty_vulnerability_criteria = ({
  groups=[],
  notice,
  cta_text,
}: i_bounty_vulnerability_criteria_props)=>{
  const [opened, set_opened] = useState<number[]>([]);

  const toggle_group = (index: number)=>{
    set_opened(current=>current.includes(index)
      ? current.filter(item=>item!=index)
      : [...current, index]);
  };

  return <section className={styles.bounty_vulnerability_criteria}>
    <div className={styles.bounty_vulnerability_criteria__inner}>
      <div className={styles.bounty_vulnerability_criteria__groups}>
        {groups.map((group, index)=>{
          const is_open = opened.includes(index);
          const content_id = `bounty-criteria-${index}`;
          const items = group.items||[];
          const item_columns = [
            items.slice(0, left_column_size),
            items.slice(left_column_size),
          ].filter(column=>!!column.length);
          const icon_src = group.type=='blocked'
            ? item_icon_src.blocked
            : item_icon_src.allowed;
          return <article
            key={`${group.title}-${index}`}
            className={styles.bounty_vulnerability_criteria__group}
            data-type={group.type||'allowed'}
            data-open={is_open||undefined}>
            <div
              className={styles.bounty_vulnerability_criteria__group_head}>
              <span className={styles.bounty_vulnerability_criteria__icon}
                aria-hidden="true">
                {group.image_src?<img
                  src={get_img_url(group.image_src)}
                  alt={group.image_alt||''}/>
                  :group.type=='blocked' ? '!' : '✓'}
              </span>
              <div
                className={styles.bounty_vulnerability_criteria__content}>
                <h4
                  className={styles.bounty_vulnerability_criteria__title}>
                  {group.title}
                </h4>
                {group.description&&<p
                  className={
                    styles.bounty_vulnerability_criteria__description
                  }>
                  {group.description}
                </p>}
              </div>
            </div>
            {!!group.items?.length&&<Accordion_panel
              open={is_open}
              id={content_id}>
              <div className={styles.bounty_vulnerability_criteria__list}>
                {item_columns.map((column, column_index)=><ul
                  key={column_index}
                  className={styles.bounty_vulnerability_criteria__column}>
                  {column.map((item, item_index)=><li
                    key={`${item}-${item_index}`}
                    className={styles.bounty_vulnerability_criteria__item}>
                    <img
                      className={
                        styles.bounty_vulnerability_criteria__item_icon
                      }
                      src={get_img_url(icon_src)}
                      alt=""
                      aria-hidden={true}
                    />
                    <span
                      className={
                        styles.bounty_vulnerability_criteria__item_text
                      }>
                      {item}
                    </span>
                  </li>)}
                </ul>)}
              </div>
            </Accordion_panel>}
            {!!group.items?.length&&<button
              type="button"
              className={styles.bounty_vulnerability_criteria__toggle}
              aria-expanded={is_open}
              aria-controls={content_id}
              onClick={()=>toggle_group(index)}>
              {is_open
                ? group.hide_text||'Hide'
                : group.show_text||'Show'}
            </button>}
          </article>;
        })}
      </div>
      {notice&&<div
        className={styles.bounty_vulnerability_criteria__notice}>
        <img
          className={styles.bounty_vulnerability_criteria__notice_icon}
          src={get_img_url(notice_icon_src)}
          alt=""
          aria-hidden={true}
        />
        <p className={styles.bounty_vulnerability_criteria__notice_text}>
          {notice}
        </p>
      </div>}
      {cta_text&&<Button
        caption={cta_text}
        to={build_support_link()}
        cls={styles.bounty_vulnerability_criteria__cta}
        round
      />}
    </div>
  </section>;
};

interface i_bounty_vulnerability_criteria_editor_props {
  value: i_bounty_vulnerability_criteria_props;
  on_change: (value: i_bounty_vulnerability_criteria_props)=>void;
  on_cancel?: ()=>void;
}

export const Bounty_vulnerability_criteria_editor = (
  props: i_bounty_vulnerability_criteria_editor_props
)=>{
  const [state, set_state] =
    useState<i_bounty_vulnerability_criteria_props>({
      ...props.value,
      groups: props.value?.groups||[],
    });

  const groups = state.groups||[];

  const update_group = (
    index: number,
    group: i_bounty_criteria_group
  )=>
  {
    const next_groups = [...groups];
    next_groups[index] = group;
    set_state({...state, groups: next_groups});
  };

  const update_item = (
    group_index: number,
    item_index: number,
    value: string
  )=>
  {
    const next_groups = [...groups];
    const current_group = next_groups[group_index];
    const items = current_group.items
      ? [...current_group.items]
      : [];
    items[item_index] = value;
    next_groups[group_index] = {
      ...current_group,
      items,
    };
    set_state({...state, groups: next_groups});
  };

  return <Editor<i_bounty_vulnerability_criteria_props>
    value={state}
    editors={[
      {key: 'notice', label: 'Notice', type: 'textarea'},
      {key: 'cta_text', label: 'CTA text', type: 'text'},
    ]}
    on_change={set_state}
    on_save={()=>props.on_change(state)}
    on_cancel={props.on_cancel}>
    <Fragment>
      <h4>Criteria groups</h4>
      {groups.map((group, group_index)=><div key={group_index}>
        <Editor<i_bounty_criteria_group>
          title={group.title||`Group ${group_index+1}`}
          value={group}
          editors={[
            {key: 'title', label: 'Title', type: 'text'},
            {
              key: 'description',
              label: 'Description',
              type: 'textarea',
            },
            {
              key: 'type',
              label: 'Type',
              type: 'selector',
              options: [
                {value: 'allowed', label: 'Allowed'},
                {value: 'blocked', label: 'Blocked'},
              ],
            },
            {key: 'image_src', label: 'Icon', type: 'text'},
            {key: 'image_alt', label: 'Icon alt', type: 'text'},
            {key: 'show_text', label: 'Show text', type: 'text'},
            {key: 'hide_text', label: 'Hide text', type: 'text'},
          ]}
          on_change={(value: i_bounty_criteria_group)=>
            update_group(group_index, value)}>
          <Fragment>
            <h5>Items</h5>
            {group.items?.map((item, item_index)=><div
              key={item_index}>
              <Editor<{text: string}>
                value={{text: item}}
                editors={[
                  {key: 'text', label: 'Item', type: 'text'},
                ]}
                on_change={(value: {text: string})=>
                  update_item(group_index, item_index, value.text)}
              />
            </div>)}
          </Fragment>
        </Editor>
        <Button
          caption="Delete group"
          size="xs"
          color="secondary"
          on_click={()=>set_state({
            ...state,
            groups: groups.filter((_, index)=>index!=group_index),
          })}
        />
        <hr/>
      </div>)}
      <Button
        caption="Add group"
        size="xs"
        on_click={()=>set_state({
          ...state,
          groups: [
            ...groups,
            {
              title: '',
              description: '',
              items: [],
              type: 'allowed',
              show_text: 'Show examples',
              hide_text: 'Hide examples',
            },
          ],
        })}
      />
    </Fragment>
  </Editor>;
};
